/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
function _createMdxContent(props) {
  const _components = Object.assign({
    h1: "h1",
    p: "p",
    code: "code",
    ul: "ul",
    li: "li",
    strong: "strong",
    h2: "h2",
    a: "a",
    pre: "pre",
    h3: "h3",
    ol: "ol",
    h4: "h4"
  }, _provideComponents(), props.components);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h1, {
      children: "ESM and transpiling"
    }), "\n", _jsxs(_components.p, {
      children: ["To support every modern front end environment, the ", _jsx(_components.code, {
        children: "@dnb/eufemia"
      }), " supports different transpiled module formats:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.code, {
          children: "ESM"
        }), " with ES5 (", _jsx(_components.strong, {
          children: "default"
        }), ")"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.code, {
          children: "ES"
        }), " with ES6"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.code, {
          children: "CJS"
        }), " with ES5"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.code, {
          children: "UMD"
        }), " with ES5"]
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      children: "Default module format"
    }), "\n", _jsxs(_components.p, {
      children: ["The ", _jsx(_components.code, {
        children: "@dnb/eufemia"
      }), " uses ", _jsx(_components.strong, {
        children: "ESM"
      }), " as the default module format. This allows us to more easily and by default ", _jsx(_components.a, {
        href: "#tree-shaking",
        children: "tree shaking"
      }), " support:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-js",
        children: "// Imports only the code needed for the button\nimport { Button } from '@dnb/eufemia'\nimport { Button } from '@dnb/eufemia/es'\n\n// Imports only the code needed for icons\nimport { question } from '@dnb/eufemia/icons'\nimport { question } from '@dnb/eufemia/es/icons'\n"
      })
    }), "\n", _jsx(_components.h2, {
      children: "CommonJS (CJS)"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://nodejs.org/",
        children: "Node.js"
      }), " uses ", _jsx(_components.a, {
        href: "https://requirejs.org",
        children: "RequireJS"
      }), " and has ", _jsx(_components.a, {
        href: "https://requirejs.org/docs/commonjs.html",
        children: "CommonJS"
      }), " as their default module format."]
    }), "\n", _jsx(_components.h3, {
      children: "SSR"
    }), "\n", _jsxs(_components.p, {
      children: ["In case you are using the ", _jsx(_components.code, {
        children: "@dnb/eufemia"
      }), " in a classic Node.js environment, like typical in SSR, you can import or require everything from the ", _jsx(_components.code, {
        children: "/cjs"
      }), " subfolder:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-js",
        children: "// Components\nimport { Button } from '@dnb/eufemia/cjs'\nconst { Button } = require('@dnb/eufemia/cjs/components')\nconst Button = require('@dnb/eufemia/cjs/components/Button')\n\n// Icons\nimport { question } from '@dnb/eufemia/cjs/icons'\nconst { question } = require('@dnb/eufemia/cjs/icons')\nconst question = require('@dnb/eufemia/cjs/icons/question')\n\n// Styles\nimport '@dnb/eufemia/cjs/style'\nrequire('@dnb/eufemia/cjs/style')\n"
      })
    }), "\n", _jsx(_components.h3, {
      children: "Next.js and Node.js version 12 and above"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "NB:"
      }), " If you use Next.js with Node.js version 12 and above, we may have to define explicitly that we want to use CommonJS:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-js",
        children: "import { Button } from '@dnb/eufemia/cjs'\nconst { Button } = require('@dnb/eufemia/cjs/components')\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["Alternatively, you can make use of ", _jsx(_components.a, {
        href: "https://www.npmjs.com/package/next-transpile-modules",
        children: "next-transpile-modules"
      }), "."]
    }), "\n", _jsx(_components.h3, {
      children: "ESM in Node.js version 10"
    }), "\n", _jsxs(_components.p, {
      children: ["You can easily use ", _jsx(_components.a, {
        href: "https://nodejs.org/api/esm.html",
        children: "ESM"
      }), " in Node.js environment."]
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsxs(_components.li, {
        children: ["Install the ", _jsx(_components.code, {
          children: "esm"
        }), " package: ", _jsx(_components.code, {
          children: "npm i esm -D"
        })]
      }), "\n", _jsxs(_components.li, {
        children: ["Call Node with an environment variable: ", _jsx(_components.code, {
          children: "NODE_OPTIONS='-r esm' node ..."
        })]
      }), "\n"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-json",
        children: "// package.json\n\"start\": \"NODE_OPTIONS='-r esm' next start ./src\"\n"
      })
    }), "\n", _jsx(_components.h4, {
      children: "Jest and ESM (Node testing environments)"
    }), "\n", _jsxs(_components.p, {
      children: ["Older Jest versions uses still CommonJS as the default module format. If you use the default ", _jsx(_components.code, {
        children: "@dnb/eufemia"
      }), " imports, then you get a mismatch between ES module and CommonJS formats. To ensure that Jest transforms your code in to CSJ, you can use the following Jest configuration ", _jsx(_components.code, {
        children: "--moduleNameMapper"
      })]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-bash",
        children: "jest --moduleNameMapper '{\"@dnb/eufemia(.*)\":\"@dnb/eufemia/cjs$1\"}'\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["or in a ", _jsx(_components.code, {
        children: "jest.config.js"
      }), " or ", _jsx(_components.code, {
        children: "jest.preset.js"
      }), " file:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-js",
        children: "  module.exports = {\n    ...\n    moduleNameMapper: { '@dnb/eufemia(.*)': '@dnb/eufemia/cjs$1' }\n  }\n"
      })
    }), "\n", _jsx(_components.h2, {
      children: "Pure ES6"
    }), "\n", _jsx(_components.p, {
      children: "In some cases you may want to import an ES6 version."
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-js",
        children: "// ES6 version of all Components\nimport { Button } from '@dnb/eufemia/es'\nimport { Button } from '@dnb/eufemia/es/components'\n\n// ES6 version of a single component\nimport Button from '@dnb/eufemia/es/components/Button'\n"
      })
    }), "\n", _jsx(_components.p, {
      children: "Make sure your environment has a production build with:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Tree Shaking"
      }), "\n", _jsx(_components.li, {
        children: "Minify and mangle the Output"
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["By default, ", _jsx(_components.a, {
        href: "https://webpack.js.org",
        children: "webpack v4"
      }), " does this fine."]
    }), "\n", _jsx(_components.h2, {
      children: "Tree shaking"
    }), "\n", _jsxs(_components.p, {
      children: ["To optimize the bundle size, we have several options. It also depends on what is used to make the bundle. Is ", _jsx(_components.a, {
        href: "https://webpack.js.org",
        children: "webpack"
      }), " used, then you may read a ", _jsx(_components.a, {
        href: "https://webpack.js.org/guides/tree-shaking",
        children: "good guide on Tree Shaking"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["If you only need / import a few components, you may consider to import them directly, without named imports (", _jsx(_components.strong, {
        children: "ES5"
      }), "):"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-js",
        children: "import MyMaskedInput from '@dnb/eufemia/components/InputMasked'\nimport MyMaskedInput from '@dnb/eufemia/cjs/components/InputMasked'\n\n// or\nimport Input from '@dnb/eufemia/components/input-masked/InputMasked'\nimport Input from '@dnb/eufemia/cjs/components/input-masked/InputMasked'\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["... or as ", _jsx(_components.strong, {
        children: "ES6"
      }), " imports:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-js",
        children: "import { InputMasked } from '@dnb/eufemia/es/components'\n"
      })
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
